import { IS_BROKER_ADMIN } from './admin-build';

export interface SharedAccountManager {
  /** The address stored on the org, and shown to that org's users. */
  email: string;
  /** How the option is labelled, and what lands in `accountManagerName`. */
  name: string;
}

/**
 * Role mailboxes that may be an organisation's account manager, alongside the
 * individuals in the `Admins` Cognito group (see `useAdminUsers`).
 *
 * The account manager is not only an internal label: `get-tenant-config` serves
 * it to the org's own users as their support contact (`useSupportContact` in the
 * frontend backs the "Increase Limit" mailto links and the Product Assistant),
 * and it receives that org's invoices, EFT hand-offs and run-limit alerts. So a
 * branded shared inbox is a legitimate choice, and often the better one — it
 * survives a colleague leaving and does not publish an individual's address to
 * every user of the client.
 *
 * Deliberately NOT sourced from Cognito. A shared inbox needs no login, and
 * putting one in `Admins` purely to make it selectable would hand whoever holds
 * that mailbox full admin-dashboard access to every organisation on the stack —
 * `Admins` gates this app (`AuthContext`) and every `/admin/*` endpoint. Being
 * selectable as a contact and being able to administer the platform are separate
 * things, so they have separate sources.
 *
 * Keyed by stack because the two admin builds serve different tenants and must
 * not offer each other's addresses.
 */
const BROKER_AI_SHARED_ACCOUNT_MANAGERS: readonly SharedAccountManager[] = [
  // Trello #397 — Marcus wants new broker organisations to point at the Broker AI
  // inbox rather than at an individual's address.
  { email: 'admin@brokerai.co.za', name: 'Broker AI Admin' },
];

const INTELLIGENT_AUTOMATION_SHARED_ACCOUNT_MANAGERS: readonly SharedAccountManager[] = [];

/**
 * `isBrokerAdmin` is a parameter (defaulting to the build flag) so the tests can
 * drive both stacks without rebuilding with a different env.
 */
export function sharedAccountManagers(
  isBrokerAdmin: boolean = IS_BROKER_ADMIN,
): readonly SharedAccountManager[] {
  return isBrokerAdmin
    ? BROKER_AI_SHARED_ACCOUNT_MANAGERS
    : INTELLIGENT_AUTOMATION_SHARED_ACCOUNT_MANAGERS;
}
